// Parser for Slack's "mrkdwn" message format.
// https://api.slack.com/reference/surfaces/formatting

export type MrkdwnNode =
  | { type: 'text'; text: string }
  | { type: 'bold' | 'italic' | 'strike'; children: MrkdwnNode[] }
  | { type: 'code'; text: string }
  | { type: 'pre'; text: string }
  | { type: 'quote'; children: MrkdwnNode[] }
  | { type: 'br' }
  | { type: 'link'; url: string; label?: string }
  | { type: 'user'; id: string; label?: string }
  | { type: 'channel'; id: string; label?: string }
  | { type: 'usergroup'; id: string; label?: string }
  | { type: 'broadcast'; name: string }
  | { type: 'emoji'; name: string; skinTone?: number }
  | { type: 'date'; ts: number; format: string; fallback: string }

export function unescapeHtml(text: string): string {
  return text.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&')
}

export function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

const FORMAT_CHARS: Record<string, 'bold' | 'italic' | 'strike'> = { '*': 'bold', _: 'italic', '~': 'strike' }
const BOUNDARY_BEFORE = /[\s([{"'“‘>*_~]/
const BOUNDARY_AFTER = /[\s)\]}"'”’.,!?:;<*_~-]/

function parseEntity(body: string): MrkdwnNode {
  const pipe = body.indexOf('|')
  const target = pipe === -1 ? body : body.slice(0, pipe)
  const label = pipe === -1 ? undefined : unescapeHtml(body.slice(pipe + 1))
  if (target.startsWith('@')) return { type: 'user', id: target.slice(1), label }
  if (target.startsWith('#')) return { type: 'channel', id: target.slice(1), label }
  if (target.startsWith('!subteam^')) return { type: 'usergroup', id: target.slice(9), label }
  if (target.startsWith('!date^')) {
    const [, ts, format] = target.split('^')
    return { type: 'date', ts: Number(ts), format: format ?? '{date}', fallback: label ?? '' }
  }
  if (target.startsWith('!')) return { type: 'broadcast', name: target.slice(1).split('^')[0] }
  return { type: 'link', url: unescapeHtml(target), label }
}

/** Parses inline formatting (no block-level constructs). */
export function parseInline(src: string): MrkdwnNode[] {
  const out: MrkdwnNode[] = []
  let text = ''
  const flush = () => {
    if (text) out.push({ type: 'text', text: unescapeHtml(text) })
    text = ''
  }

  let i = 0
  while (i < src.length) {
    const ch = src[i]

    if (ch === '\n') {
      flush()
      out.push({ type: 'br' })
      i++
      continue
    }

    if (ch === '`') {
      const end = src.indexOf('`', i + 1)
      if (end > i + 1 && !src.slice(i + 1, end).includes('\n')) {
        flush()
        out.push({ type: 'code', text: unescapeHtml(src.slice(i + 1, end)) })
        i = end + 1
        continue
      }
    }

    if (ch === '<') {
      const end = src.indexOf('>', i + 1)
      if (end > i + 1 && !src.slice(i + 1, end).includes('\n')) {
        flush()
        out.push(parseEntity(src.slice(i + 1, end)))
        i = end + 1
        continue
      }
    }

    if (ch === ':') {
      const m = /^:([a-z0-9_+'\-]+):(?::skin-tone-([2-6]):)?/i.exec(src.slice(i))
      if (m) {
        flush()
        out.push({ type: 'emoji', name: m[1].toLowerCase(), skinTone: m[2] ? Number(m[2]) : undefined })
        i += m[0].length
        continue
      }
    }

    const fmt = FORMAT_CHARS[ch]
    if (fmt) {
      const before = i === 0 ? ' ' : src[i - 1]
      const next = src[i + 1]
      if (BOUNDARY_BEFORE.test(before) && next && !/\s/.test(next) && next !== ch) {
        // Find a closing delimiter on the same line followed by a boundary.
        let j = i + 1
        while (j < src.length && src[j] !== '\n') {
          if (src[j] === ch && !/\s/.test(src[j - 1]) && (j + 1 >= src.length || BOUNDARY_AFTER.test(src[j + 1]))) break
          // Don't let formatting span across code or entities.
          if (src[j] === '`' || src[j] === '<') {
            const close = src.indexOf(src[j] === '`' ? '`' : '>', j + 1)
            if (close !== -1) j = close
          }
          j++
        }
        if (j < src.length && src[j] === ch && j > i + 1) {
          flush()
          out.push({ type: fmt, children: parseInline(src.slice(i + 1, j)) })
          i = j + 1
          continue
        }
      }
    }

    text += ch
    i++
  }
  flush()
  return out
}

/** Parses a full mrkdwn string, including ``` code blocks and > quotes. */
export function parseMrkdwn(src: string): MrkdwnNode[] {
  if (!src) return []
  const out: MrkdwnNode[] = []
  const parts = src.split('```')
  parts.forEach((part, index) => {
    const isCode = index % 2 === 1 && index < parts.length - 1
    if (isCode) {
      out.push({ type: 'pre', text: unescapeHtml(part.replace(/^\n/, '').replace(/\n$/, '')) })
      return
    }
    if (index % 2 === 1) part = '```' + part // unmatched fence: treat literally
    if (!part) return

    // Group lines into quote / non-quote runs.
    const lines = part.split('\n')
    let run: string[] = []
    let quoting = false
    const emit = () => {
      if (!run.length) return
      const body = run.join('\n')
      if (quoting) out.push({ type: 'quote', children: parseInline(body) })
      else out.push(...parseInline(body))
      run = []
    }
    lines.forEach((line, li) => {
      const q = /^(&gt;|>)\s?/.exec(line)
      const isQuote = !!q
      if (isQuote !== quoting) {
        emit()
        // A newline separating a quote from following text is implied by the block.
        quoting = isQuote
      }
      run.push(isQuote ? line.slice(q![0].length) : line)
      if (li === lines.length - 1) emit()
    })
  })
  // Trim leading/trailing breaks introduced next to blocks.
  while (out[0]?.type === 'br') out.shift()
  while (out[out.length - 1]?.type === 'br') out.pop()
  return out
}

export interface MentionLookup {
  user?: (id: string) => string | undefined
  channel?: (id: string) => string | undefined
  usergroup?: (id: string) => string | undefined
}

/** Renders mrkdwn to plain text (for notifications, previews and editing). */
export function toPlainText(src: string, lookup: MentionLookup = {}, opts: { keepMarkup?: boolean } = {}): string {
  const render = (nodes: MrkdwnNode[]): string =>
    nodes
      .map((n) => {
        switch (n.type) {
          case 'text':
            return n.text
          case 'br':
            return '\n'
          case 'bold':
            return opts.keepMarkup ? `*${render(n.children)}*` : render(n.children)
          case 'italic':
            return opts.keepMarkup ? `_${render(n.children)}_` : render(n.children)
          case 'strike':
            return opts.keepMarkup ? `~${render(n.children)}~` : render(n.children)
          case 'code':
            return opts.keepMarkup ? `\`${n.text}\`` : n.text
          case 'pre':
            return opts.keepMarkup ? `\`\`\`${n.text}\`\`\`` : n.text
          case 'quote':
            return render(n.children)
              .split('\n')
              .map((l) => `> ${l}`)
              .join('\n')
          case 'link':
            return n.label && n.label !== n.url ? (opts.keepMarkup ? `${n.label} (${n.url})` : n.label) : n.url.replace(/^mailto:/, '')
          case 'user':
            return `@${lookup.user?.(n.id) ?? n.label ?? n.id}`
          case 'channel':
            return `#${lookup.channel?.(n.id) ?? n.label ?? n.id}`
          case 'usergroup':
            return lookup.usergroup?.(n.id) ? `@${lookup.usergroup(n.id)}` : (n.label ?? `@${n.id}`)
          case 'broadcast':
            return `@${n.name}`
          case 'emoji':
            return `:${n.name}:`
          case 'date':
            return n.fallback || new Date(n.ts * 1000).toLocaleString()
        }
      })
      .join('')
  return render(parseMrkdwn(src))
}

export interface MentionResolver {
  /** Returns a user id for a handle typed after "@", if one matches. */
  user: (handle: string) => string | undefined
  /** Returns a channel id for a name typed after "#", if one matches. */
  channel: (name: string) => string | undefined
}

/**
 * Converts text typed in the composer to Slack wire format: escapes control
 * characters and turns @handle / #channel / @here into proper entities.
 */
export function toSlackText(
  input: string,
  resolve: MentionResolver,
  /** Exact strings inserted by autocomplete (e.g. "@Jane Doe") and their wire form ("<@U123>"). */
  known: { text: string; replacement: string }[] = [],
): string {
  let escaped = escapeHtml(input)
  const sorted = [...known].sort((a, b) => b.text.length - a.text.length)
  sorted.forEach((k, i) => {
    escaped = escaped.split(escapeHtml(k.text)).join(`\u0001${i}\u0001`)
  })
  return escaped
    .replace(/(^|[\s(])@(here|channel|everyone)\b/g, (_m, pre, name) => `${pre}<!${name}>`)
    .replace(/(^|[\s(])@([\w.\-]+)/g, (m, pre, handle) => {
      const id = resolve.user(handle)
      return id ? `${pre}<@${id}>` : m
    })
    .replace(/(^|[\s(])#([\w\-]+)/g, (m, pre, name) => {
      const id = resolve.channel(name)
      return id ? `${pre}<#${id}|${name}>` : m
    })
    .replace(/\u0001(\d+)\u0001/g, (_m, i) => sorted[Number(i)].replacement)
}
